import { groupBy, isEmpty, reject } from 'lodash';
import moment, { duration, Moment } from 'moment';
import { IntlShape } from 'react-intl';
import { Holiday } from 'api/holiday-calendar/types';
import { User, UserId, UserStatus } from 'api/models';
import { Project, ProjectFinancialsContractType } from 'api/project';
import {
  Configuration,
  EnabledSourcesConfiguration,
  NonBillableHoursFlagSettingConfiguration,
  ProjectReportTimeSettings,
} from 'api/timesheets/configuration/configuration';
import { withPredicatedParseError } from 'components/atoms/input-duration';
import { Option } from 'components/atoms/select/types';
import {
  Minutes,
  WorkingDaysConfiguration,
} from 'components/organisms/user-capacity';
import { date as dateByDay, DayOfWeek, getStartOfWeek } from 'core/day-of-week';
import { getDays } from 'utils/date-field/get-days-array';
import { fullName } from 'utils/full-name';
import { checkIfCurrentDateHasHoliday } from 'utils/holiday-calendar-utils';
import { sortByText } from 'utils/sort-by-text';
import { sortByUserStatus } from 'utils/sort-by-user-status';
import { checkIfCurrentDateHasTimeOff } from 'utils/time-off-utils';
import {
  FilterViewType,
  TimeEntryState,
  TimesheetReminder,
  TimesheetStatus,
  TimesheetTableTimeEntry,
  UserLevelSummary,
  UserTimesheet,
  WeekPreset,
} from '../types';

export { getDays };

const numberOfWorkingDaysFromDate = (
  startDate: Moment,
  numberOfDays: number,
  workingDays: WorkingDaysConfiguration,
): number => {
  return [...Array(numberOfDays)].reduce((noOfDays, _, index) => {
    const dayName = moment(startDate)
      .add(index, 'day')
      .locale('en')
      .format('dddd');

    return workingDays[dayName] ? noOfDays + 1 : noOfDays;
  }, 0);
};

export const numberOfWorkingDaysBetweenDates = (
  startDate: Moment,
  endDate: Moment,
  workingDays: WorkingDaysConfiguration,
): number => {
  if (endDate.isBefore(startDate)) {
    return 0;
  }
  const totalNoOfDays = endDate.diff(startDate, 'day') + 1;

  let noOfWorkingDays = 0;
  const noOfWorkingDaysInFirstWeek = numberOfWorkingDaysFromDate(
    startDate,
    7,
    workingDays,
  );

  noOfWorkingDays = noOfWorkingDaysInFirstWeek * Math.floor(totalNoOfDays / 7);
  const remainingDays = totalNoOfDays % 7;

  noOfWorkingDays += numberOfWorkingDaysFromDate(
    endDate.subtract(remainingDays - 1, 'day'),
    remainingDays,
    workingDays,
  );

  return noOfWorkingDays;
};

export const isNonWorkingDay = (
  date: Moment,
  workingDays: WorkingDaysConfiguration,
): boolean => !workingDays[date.locale('en').format('dddd')];

export const getDateAfterSomeNumberOfWorkingDays = (
  startDate: Moment,
  numberOfDays: number,
  workingDays: WorkingDaysConfiguration,
): Moment => {
  let daysLeft = numberOfDays;
  let currentDate = startDate.clone();

  while (daysLeft > 0) {
    currentDate = currentDate.add(1, 'day');
    if (!isNonWorkingDay(currentDate, workingDays)) {
      daysLeft -= 1;
    }
  }

  return currentDate;
};

export const getColumnClassesForTimeCell = (
  date: Moment,
  workingDays: WorkingDaysConfiguration,
  activeColumn?: string,
  currentUser?: User,
  holidays?: Holiday[],
): string => {
  const classNames: string[] = [];

  if (isNonWorkingDay(date, workingDays)) {
    classNames.push('weekend-cell');
  }
  if (date.day() !== 0 && date.day() !== 6) {
    if (checkIfCurrentDateHasTimeOff(currentUser?.userTimeOffDetails, date)) {
      classNames.push('timeoff-cell');
    }
    if (checkIfCurrentDateHasHoliday(holidays, date)) {
      classNames.push('holiday-cell');
    }
  }
  if (moment().isSame(date, 'day')) {
    classNames.push('today-cell');
  }
  if (activeColumn === date.format('YYYY-MM-DD')) {
    classNames.push('active-column');
  }

  return classNames.join(' ');
};

export const getCellClassesForTimesheetCell = (
  date: Moment,
  record: UserLevelSummary,
): string => {
  const classNames: string[] = [];

  if (
    record.status !== TimesheetStatus.NotSubmitted &&
    checkIfCurrentDateHasTimeOff(record.user?.userTimeOffDetails, date)
  ) {
    classNames.push('timeoff-cell');
  }

  if (record.status === TimesheetStatus.NotSubmitted) {
    classNames.push('non-submitted-cell');
  } else {
    classNames.push('hoverable-cell');
  }

  return classNames.join(' ');
};

export const getTotalMinutesFromEntries = (
  entries: TimesheetTableTimeEntry[],
): number => (entries ?? []).reduce((total, entry) => total + entry.minutes, 0);

export const getTotalMinutesForTask = (
  days: Record<string, TimesheetTableTimeEntry[]>,
): number =>
  Object.values(days).reduce(
    (total, childEntries) => total + getTotalMinutesFromEntries(childEntries),
    0,
  );

export const getTotalBillableMinutesFromEntries = (
  entries: TimesheetTableTimeEntry[],
): number =>
  entries.reduce(
    (total, entry) => (entry.billable ? total + entry.minutes : total),
    0,
  );

export const getTotalBillableMinutesForTask = (
  days: Record<string, TimesheetTableTimeEntry[]>,
): number =>
  Object.values(days).reduce(
    (total, childEntries) =>
      total + getTotalBillableMinutesFromEntries(childEntries),
    0,
  );

export const getTotalNonBillableMinutesFromEntries = (
  entries: TimesheetTableTimeEntry[],
): number =>
  entries.reduce(
    (total, entry) => (!entry.billable ? total + entry.minutes : total),
    0,
  );

export const getTotalNonBillableMinutesForTask = (
  days: Record<string, TimesheetTableTimeEntry[]>,
): number =>
  Object.values(days).reduce(
    (total, childEntries) =>
      total + getTotalNonBillableMinutesFromEntries(childEntries),
    0,
  );

export const getTotalBillableNonBillableMinutes = (
  entries: TimesheetTableTimeEntry[],
) => {
  const totalMinutes = getTotalMinutesFromEntries(entries) || 0;
  const totalBillableMinutes = getTotalBillableMinutesFromEntries(entries) || 0;

  const totalNonBillableMinutes =
    getTotalNonBillableMinutesFromEntries(entries) || 0;

  return { totalMinutes, totalBillableMinutes, totalNonBillableMinutes };
};

export const getTotalBillableNonBillableMinutesForTask = (
  days: Record<string, TimesheetTableTimeEntry[]>,
) => {
  const totalBillableHours = getTotalBillableMinutesForTask(days);

  const totalNonBillableHours = getTotalNonBillableMinutesForTask(days);

  return {
    totalBillableHours,
    totalNonBillableHours,
  };
};

export const getTotalBillableMinutes = (
  entries: TimesheetTableTimeEntry[],
): number =>
  getTotalMinutesFromEntries(entries.filter((entry) => entry.billable));

export const getTotalNonBillableMinutes = (
  entries: TimesheetTableTimeEntry[],
): number =>
  getTotalMinutesFromEntries(entries.filter((entry) => !entry.billable));

export const calculateUtilization = (
  billableMinutes: number,
  capacityMinutes: Minutes,
): number =>
  capacityMinutes === 0 ? -1 : (billableMinutes / capacityMinutes) * 100;

export const calculatePercentage = (
  minutes: number,
  billableMinutes: number,
  nonBillableMinutes: number,
): number => {
  const total = billableMinutes + nonBillableMinutes;

  return total === 0 ? 0 : (minutes / total) * 100;
};

export const getTimesheetsByFilter = (
  timesheets: UserTimesheet[],
  filter: FilterViewType,
  listOfUsersMappedUnderYou: number[],
  listOfProjectsIOwn: number[],
): UserTimesheet[] => {
  switch (filter) {
    case FilterViewType.Approved:
      return timesheets.filter(
        (timesheet) => timesheet.status === TimesheetStatus.Approved,
      );
    case FilterViewType.Submitted:
      return timesheets.filter(
        (timesheet) => timesheet.status === TimesheetStatus.Submitted,
      );
    case FilterViewType.NotSubmitted:
      return timesheets.filter(
        (timesheet) => timesheet.status === TimesheetStatus.NotSubmitted,
      );
    case FilterViewType.Rejected:
      return timesheets.filter(
        (timesheet) => timesheet.status === TimesheetStatus.Rejected,
      );
    case FilterViewType.MyTeam:
      return timesheets.filter((timesheet) =>
        listOfUsersMappedUnderYou.includes(timesheet?.user?.userId),
      );
    case FilterViewType.ProjectsIOwn:
      return timesheets.filter((timesheet) =>
        listOfProjectsIOwn.includes(timesheet.project?.id),
      );
    default:
      return timesheets;
  }
};
export const getCountOfTimesheetsByFilter = (
  timesheets: UserTimesheet[],
  filter: FilterViewType,
  listOfUsersMappedUnderYou: number[],
  listOfProjectsIOwn: number[],
): number =>
  (
    getTimesheetsByFilter(
      timesheets,
      filter,
      listOfUsersMappedUnderYou,
      listOfProjectsIOwn,
    ) ?? []
  ).length;

export const getWeekStartDate = (week: string): Moment => {
  switch (week as WeekPreset) {
    case WeekPreset.ThisWeek:
      return getStartOfWeek(DayOfWeek.Monday, moment());
    case WeekPreset.PreviousWeek:
      return getStartOfWeek(DayOfWeek.Monday, moment().subtract(1, 'week'));
    default:
      return getStartOfWeek(DayOfWeek.Monday, moment(week, 'YYYY-MM-DD'));
  }
};

export const getWeekPreset = (
  startDate: Moment,
  allowedPresets: WeekPreset[],
): string => {
  return (
    allowedPresets
      .map((preset): [WeekPreset, Moment] => [
        preset,
        getWeekStartDate(preset as string),
      ])
      .find(([_, presetDate]) => presetDate.isSame(startDate, 'day'))?.[0] ??
    startDate.format('YYYY-MM-DD')
  );
};

export const getTimesheetUrl = ({
  currentUserId,
  userId,
  startDate,
  projectId,
}: {
  currentUserId: UserId;
  userId?: UserId;
  startDate?: string;
  projectId?: number;
}): string => {
  const [_, _currentUserId] = currentUserId;

  if (userId === undefined || _currentUserId === userId[1]) {
    return `/timesheets/${startDate || WeekPreset.ThisWeek}/my-timesheet`;
  }

  if (projectId) {
    return `/timesheets/${startDate || WeekPreset.ThisWeek}/approvals/${
      userId[1]
    }?projectId=${projectId}`;
  }

  return `/timesheets/${startDate || WeekPreset.ThisWeek}/approvals/${
    userId[1]
  }`;
};

export const isTimeEntryNotLoaded = (entry: TimesheetTableTimeEntry): boolean =>
  entry.inState !== TimeEntryState.Loaded;

export const isTimeEntryInCreating = (
  entry: TimesheetTableTimeEntry,
): boolean => entry.inState === TimeEntryState.Creating;

export const isTimeEntryPartiallyFilled = (
  entry: TimesheetTableTimeEntry,
  mandateCategory: boolean,
): boolean =>
  entry.inState === TimeEntryState.PartiallyFilled ||
  (mandateCategory && (!entry.category || isEmpty(entry.category)));

export const withLessThanOneDayParser = withPredicatedParseError<
  number,
  'greater_than_one_day'
>('greater_than_one_day', (x) => x > duration(1, 'day').asMinutes());

export const mondayOfWeek = (value: Moment = moment()): Moment =>
  dateByDay(DayOfWeek.Monday, value);

export const mondayOfWeekForApproval = (currDay): Moment => {
  if (currDay.format('dddd') === DayOfWeek.Sunday) {
    return currDay.subtract(6, 'days');
  }

  return dateByDay(DayOfWeek.Monday, currDay);
};

export const endOfWeek = (day: Moment): Moment =>
  dateByDay(DayOfWeek.Monday, day).add(6, 'days');

export const weekFormatter = (currentValue: moment.MomentInput): string => {
  return `${mondayOfWeek(moment(currentValue)).format(
    'DD MMM YYYY',
  )} - ${endOfWeek(moment(currentValue)).format('DD MMM YYYY')}`;
};

export const checkForProjectStatus = (
  option,
  projectReportTimeSettings: ProjectReportTimeSettings,
  isResourceManagement?: boolean,
) => {
  if (
    projectReportTimeSettings.projectWithStatuses.length === 0 ||
    isResourceManagement ||
    !option.statusValue
  ) {
    return true;
  }

  return projectReportTimeSettings.projectWithStatuses.includes(
    option.statusValue,
  );
};

export const getProjectSelectOptions = (
  projectOptions,
  myProjectIds: number[],
  translate,
  projectReportTimeSettings?: ProjectReportTimeSettings,
  isResourceManagement?: boolean,
) => {
  const NON_PROJECT_ACTIVITY_ID = 1;
  const byNonProject = (option) => option.value === NON_PROJECT_ACTIVITY_ID;
  const [nonProject] = projectOptions?.filter(byNonProject) ?? [];
  const otherProjects = reject(projectOptions, byNonProject).filter(
    (projectOption) =>
      projectReportTimeSettings
        ? checkForProjectStatus(
            projectOption,
            projectReportTimeSettings,
            isResourceManagement,
          )
        : true,
  );

  const predicateByMyProject = (projectValue: Option<Project>): boolean =>
    myProjectIds.includes(projectValue.value as number);

  const projectOption = {
    id: 'projects',
    label: translate('other_project'),
    options: otherProjects.filter(
      (projectValue) => !predicateByMyProject(projectValue),
    ),
  };
  const nonProjectOption = {
    id: 'nonProject',
    options: nonProject ? [nonProject] : [],
  };

  const yourProject = {
    id: 'yourProject',
    label: translate('your_project'),
    options: otherProjects.filter((projectValue) =>
      predicateByMyProject(projectValue),
    ),
  };

  if (isResourceManagement) {
    return [nonProjectOption, yourProject, projectOption];
  }

  const projectSelectOptions = projectReportTimeSettings?.yourProjectOnly
    ? [nonProjectOption, yourProject]
    : [nonProjectOption, yourProject, projectOption];

  return projectSelectOptions;
};

export const getLatestReminderAt = (reminders: TimesheetReminder[]) => {
  if (!reminders) {
    return 0;
  }
  const sorted = reminders
    .map((reminder) => reminder.createdAt)
    .sort((a, b) => b - a);

  return sorted.length > 0 ? sorted[0] : 0;
};

const groupEntriesByDay = (
  entries: TimesheetTableTimeEntry[],
  isManagerApproval: boolean,
) => {
  const groupedEntry = groupBy(entries, 'date');

  return Object.keys(groupedEntry).reduce((days, entryDate) => {
    days[entryDate] = isManagerApproval
      ? { totalMinutes: getTotalMinutesFromEntries(groupedEntry[entryDate]) }
      : getTotalBillableNonBillableMinutes(groupedEntry[entryDate]);

    return days;
  }, {});
};

export const calculateUserLevelSummaries = (
  userTimesheets: UserTimesheet[],
  isManagerApproval: boolean,
): UserLevelSummary[] => {
  const userLevelSummaries = userTimesheets.reduce((acc, userTimesheetNext) => {
    const invalidData =
      userTimesheetNext?.user?.userStatus === UserStatus.Inactive &&
      userTimesheetNext?.entries?.length === 0;

    if (!invalidData) {
      acc.push({
        user: userTimesheetNext.user,
        userId: userTimesheetNext.user.userId,
        startDate: userTimesheetNext.startDate,
        status: userTimesheetNext.status,
        updatedAt: userTimesheetNext.updatedAt,
        lastReminderAt:
          userTimesheetNext.status === TimesheetStatus.NotSubmitted
            ? getLatestReminderAt(userTimesheetNext.reminders)
            : 0,
        days: groupEntriesByDay(userTimesheetNext.entries, isManagerApproval),
        projectId: userTimesheetNext.project?.id,
        projectName: userTimesheetNext.project?.name,
        reminders:
          userTimesheetNext.status === TimesheetStatus.NotSubmitted
            ? userTimesheetNext.reminders ?? []
            : [],
      });
    }

    return acc;
  }, [] as Array<UserLevelSummary>);
  const ordered = (timesheets) => {
    return (timesheets ?? []).sort((timesheetA, timesheetB) =>
      sortByText(fullName(timesheetA.user), fullName(timesheetB.user)),
    );
  };

  const grouped = groupBy(userLevelSummaries, 'status');

  const orderedUserTimesheets: UserLevelSummary[] = [
    ...ordered(grouped[TimesheetStatus.Submitted]),
    ...ordered(grouped[TimesheetStatus.Rejected]),
    ...ordered(grouped[TimesheetStatus.Approved]),
    ...ordered(grouped[TimesheetStatus.NotSubmitted]),
  ];

  const sortedUserSummaries = orderedUserTimesheets.sort(sortByUserStatus);

  return sortedUserSummaries;
};

const currentWeekDays = (startDate: Moment) =>
  getDays(startDate, 7).map((x) => x.format('YYYY-MM-DD'));

export const isEntireWeekFreezed = (
  startDate: Moment,
  configuration: Configuration,
) => {
  if (configuration.lockSetting.freeze.enabled) {
    return moment(currentWeekDays(startDate)[6]).isSameOrBefore(
      moment(configuration.lockSetting.freeze.enableAfter),
    );
  }

  return false;
};

export const isDayFreezedInWeek = (
  configuration: Configuration,
  startDate: Moment,
) => {
  if (configuration.lockSetting.freeze.enabled) {
    return currentWeekDays(startDate).includes(
      configuration.lockSetting.freeze.enableAfter ?? '',
    );
  }

  return false;
};

export const renderWeekDate = (day: Moment, format = 'DD MMM YY'): string => {
  return `${mondayOfWeek(day).format(format)} - ${endOfWeek(day).format(
    format,
  )}`;
};

export const getPlaceHolderText = (
  timesheetEntriesConfig: EnabledSourcesConfiguration,
  intl: IntlShape,
) => {
  const options: string[] = [];

  if (timesheetEntriesConfig.PHASE) {
    options.push('phase');
  }
  if (timesheetEntriesConfig.TASK || timesheetEntriesConfig.MILESTONE) {
    options.push('task');
  }
  if (timesheetEntriesConfig.ADHOC) {
    options.push('activity');
  }

  return `Add ${intl.formatList(options, { type: 'unit' })}`;
};

export const isNonBillableHoursFlagEnabled = (
  financialContractType?: ProjectFinancialsContractType,
  nonBillableHoursFlagSetting?: NonBillableHoursFlagSettingConfiguration,
) => {
  if (
    nonBillableHoursFlagSetting === undefined ||
    financialContractType === undefined
  ) {
    return false;
  }
  switch (financialContractType) {
    case ProjectFinancialsContractType.FIXED:
      return nonBillableHoursFlagSetting.nonBillableHoursFlagForFixedFeeProjectsEnabled;
    case ProjectFinancialsContractType.TM:
      return nonBillableHoursFlagSetting.nonBillableHoursFlagForTimeAndMaterialProjectsEnabled;
    case ProjectFinancialsContractType.SUBSCRIPTION:
      return nonBillableHoursFlagSetting.nonBillableHoursFlagForSubscriptionProjectsEnabled;
    default:
      return false;
  }
};
